import { useCallback, useEffect, useState, type DependencyList } from 'react';

export type FetchStatus = 'idle' | 'loading' | 'success' | 'failure';

export interface UseFetchOptions<TArgs extends any[], TData> {
  immediate?: boolean;
  immediateArgs?: TArgs;
  deps?: DependencyList;
  initialData?: TData;
  onError?: (error: unknown) => void;
  rethrow?: boolean;
  timeoutMs?: number;
  disableRunOnDeps?: boolean;
}

export interface UseFetchResult<TData, TArgs extends any[]> {
  data: TData | undefined;
  error: unknown;
  status: FetchStatus;
  isIdle: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isFailure: boolean;
  run: (...args: TArgs) => Promise<TData | undefined>;
  reset: () => void;
}

export const useFetch = <TArgs extends any[], TData>(
  fetcher: (...args: TArgs) => Promise<TData>,
  options?: UseFetchOptions<TArgs, TData>
): UseFetchResult<TData, TArgs> => {
  const {
    immediate,
    immediateArgs,
    deps = [],
    initialData,
    onError,
    rethrow = false,
    timeoutMs,
    disableRunOnDeps = false,
  } = options ?? {}; 

  const [data, setData] = useState<TData | undefined>(initialData);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState<FetchStatus>('idle');

  const run = useCallback(
    async (...args: TArgs) => {
      setStatus('loading');
      setError(null);
      try {
        const result = await Promise.race([
          fetcher(...args),
          ...(timeoutMs ? [
            new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error('Tempo limite excedido. Tente novamente.')), timeoutMs);
            }),
          ] : []),
        ]);
        setData(result);
        setStatus('success');
        return result;
      } catch (err) {
        setError(err);
        setStatus('failure');
        onError?.(err);
        if (rethrow) {
          throw err;
        }
        return undefined;
      }
    },
    [fetcher, onError, rethrow, timeoutMs]
  );

  useEffect(() => {
    if (immediate && !disableRunOnDeps) {
      void run(...((immediateArgs ?? []) as unknown as TArgs));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, immediate, immediateArgs, ...deps]);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setData(initialData);
  }, [initialData]);

  return {
    data,
    error,
    status,
    isIdle: status === 'idle',
    isLoading: status === 'loading',
    isSuccess: status === 'success',
    isFailure: status === 'failure',
    run,
    reset,
  };
};

export default useFetch;
